import { Spinner, UserIcon } from '../../../../../assets/icon/svg';
import ProfileTab from '../../../account-setting/profile-tab';
import getFullName from '../../../../../hooks/common/use-fullname';
import { getInitials } from '../../../../../utils/get-initials.utils';
import { useSelector } from 'react-redux';
import { selectUserId } from '../../../../../features/auth/auth-slice';
import { buttonClass } from '../../../../../constant/custom-style';
import { useCreateUser } from '../../../../../hooks/actions/use-create-user';
import { UserInfo } from '../../../../../api/user/user-api-state';
import ConfirmDefaultModal from '../../../../global/modal/confirm-default-modal';
import { useState } from 'react';
import UserAction from './user-action';

interface Props {
    userData: UserInfo;
    errorMessage: string;
    isLoadingUserData: boolean;
    isErrorUserData: boolean;
}

export default function ProfileView({ userData, errorMessage, isErrorUserData, isLoadingUserData }: Props) {
    
    const userName = userData
        ? getFullName({
            firstName: userData.firstname,
            lastName: userData.lastname,
            middleName: userData.middlename,
            suffix: userData?.suffix,
        })
        : '';
    const firstAndLastLetter = userData ? getInitials(userData.firstname, userData.lastname) : '';
    const userDepartment = userData?.department?.name ?? '';

    return (
        <>
            {isLoadingUserData ? (
                <div className='h-[250px] flex items-center justify-center'>
                    <div className="flex justify-center items-center h-full w-full">
                        <Spinner color={'#186cb2'} size={30} />
                    </div>
                </div>
            ) : isErrorUserData ? (
                <div className='h-[250px] flex items-center justify-center'>
                    <div className="flex justify-center items-center h-full w-full">
                        <p className='text-black text-sm'>{errorMessage}</p>
                    </div>
                </div>
            ) : userData ? (
                <div className="space-y-4 p-3 sm:p-4">
                    {/* PROFILE HEADER */}
                    <section className="relative overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                        <div className="absolute inset-0 bg-gradient-to-br from-[#186cb2] via-[#1d78c2] to-[#0f5a99]" />
                        <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full border border-white/10 bg-white/5" />
                        <div className="absolute -bottom-14 -left-8 h-32 w-32 rounded-full border border-white/10 bg-white/5" />
                        <div className="relative px-3 py-3 sm:px-4 sm:py-3.5">
                            <div className="flex items-center gap-3">
                                <div className="relative flex-shrink-0">
                                    <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-white/30 bg-white/15 text-sm font-bold tracking-wide text-white shadow-md backdrop-blur-sm sm:h-14 sm:w-14 sm:text-base">
                                        {firstAndLastLetter}
                                    </div>
                                    <span className={`absolute -bottom-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full border-2 border-[#186cb2] ${userData.is_active ? 'bg-green-400' : 'bg-red-400'}`}>
                                        <span className="h-1.5 w-1.5 rounded-full bg-white" />
                                    </span>
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="min-w-0">
                                            <h2 className="truncate text-sm font-bold tracking-tight text-white sm:text-base">{userName}</h2>
                                            <p className="mt-0.5 truncate text-[10px] text-blue-100 sm:text-[11px]">{userData.position || 'No position assigned'}</p>
                                        </div>
                                        <span className={`hidden flex-shrink-0 items-center gap-1.5 rounded-full border px-2 py-1 text-[9px] font-semibold sm:inline-flex ${userData.is_active ? 'border-green-300/20 bg-green-400/15 text-green-100' : 'border-red-300/20 bg-red-400/15 text-red-100'}`}>
                                            <span className={`h-1.5 w-1.5 rounded-full ${userData.is_active ? 'bg-green-300' : 'bg-red-300'}`} />
                                            {userData.is_active ? 'Active Account' : 'Inactive Account'}
                                        </span>
                                    </div>
                                    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                                        <div className="flex items-center gap-1 rounded-md border border-white/10 bg-white/10 px-2 py-1 backdrop-blur-sm">
                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-3 w-3 text-blue-100">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M3 21h18M5 21V7l7-4 7 4v14M9 21v-6h6v6" />
                                            </svg>
                                            <span className="max-w-[140px] truncate text-[9px] font-medium text-white">{userDepartment || 'No department assigned'}</span>
                                        </div>
                                        <div className="flex items-center gap-1 rounded-md border border-white/10 bg-white/10 px-2 py-1 backdrop-blur-sm">
                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-3 w-3 text-blue-100">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z" />
                                            </svg>
                                            <span className="max-w-[140px] truncate text-[9px] font-medium text-white">{userData.role?.name || 'No role assigned'}</span>
                                        </div>
                                        <span className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-[9px] font-semibold sm:hidden ${userData.is_active ? 'bg-green-400/15 text-green-100' : 'bg-red-400/15 text-red-100'}`}>
                                            <span className={`h-1.5 w-1.5 rounded-full ${userData.is_active ? 'bg-green-300' : 'bg-red-300'}`} />
                                            {userData.is_active ? 'Active' : 'Inactive'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="relative border-t border-gray-100 bg-white px-4 py-2.5 sm:px-5">
                            <div className='flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[10px] text-gray-500 sm:justify-start'>
                                <span className="flex items-center gap-1.5">
                                    <span className="font-medium text-gray-700">Agent Code:</span>
                                    <span>{userData.agent_code || '-'}</span>
                                </span>
                                <span className="hidden h-3 w-px bg-gray-200 sm:block" />
                                <span className="flex items-center gap-1.5">
                                    <span className="font-medium text-gray-700">Location:</span>
                                    <span>{userData.location?.name || '-'}</span>
                                </span>
                            </div>
                        </div>
                    </section>
                    {/* PERSONAL INFORMATION */}
                    <section className="overflow-hidden rounded-xl border border-gray-200 bg-white">
                        <div className="border-b border-gray-100 bg-gray-50 px-4 py-3">
                            <div className="flex items-start gap-2.5">
                                <div className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-[#186cb2]/10 text-[#186cb2]">
                                    <UserIcon width={15} height={15} />
                                </div>
                                <div>
                                    <p className="text-xs font-bold text-gray-800">
                                        Personal Information
                                    </p>
                                    <p className="mt-0.5 text-[10px] text-gray-400">
                                        Employee personal and contact information.
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 gap-2 p-3 sm:grid-cols-2 lg:grid-cols-3">
                            <ProfileTab
                                labelName="First Name"
                                labelData={userData.firstname ?? '-'}
                            />
                            <ProfileTab
                                labelName="Middle Name"
                                labelData={userData.middlename ?? '-'}
                            />
                            <ProfileTab
                                labelName="Last Name"
                                labelData={userData.lastname ?? '-'}
                            />
                            <ProfileTab
                                labelName="Suffix"
                                labelData={userData.suffix ?? '-'}
                            />
                            <ProfileTab
                                labelName="Phone Number"
                                labelData={userData.phoneNumber ?? '-'}
                            />
                            <ProfileTab
                                labelName="Email Address"
                                labelData={userData.email ?? '-'}
                            />
                        </div>
                    </section>
                    {/* ACCOUNT INFORMATION */}
                    <section className="overflow-hidden rounded-xl border border-gray-200 bg-white">
                        <div className="border-b border-gray-100 bg-gray-50 px-4 py-3">
                            <div className="flex items-start gap-2.5">
                                <div className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-[#186cb2]/10 text-[#186cb2]">
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 7h16M4 12h16M4 17h10" />
                                    </svg>
                                </div>
                                <div>
                                    <p className="text-xs font-bold text-gray-800">
                                        Account Information
                                    </p>
                                    <p className="mt-0.5 text-[10px] text-gray-400">
                                        Employee role, work assignment, and account details.
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 gap-2 p-3 sm:grid-cols-2 lg:grid-cols-3">
                            <ProfileTab
                                labelName="Position"
                                labelData={userData.position ?? '-'}
                            />
                            <ProfileTab
                                labelName="Role"
                                labelData={userData.role?.name ?? '-'}
                            />
                            <ProfileTab
                                labelName="Department"
                                labelData={userData.department?.name ?? '-'}
                            />
                            <ProfileTab
                                labelName="Location"
                                labelData={userData.location?.name ?? '-'}
                            />
                            <ProfileTab
                                labelName="Agent Code"
                                labelData={userData.agent_code ?? '-'}
                            />
                            {userData.reporting_to?.name && (
                                <ProfileTab
                                    labelName="Reporting Head"
                                    labelData={userData.reporting_to.name}
                                />
                            )}
                        </div>
                    </section>
                </div>
            ) : null}
        </>
    )
}
